/**
 * Receipts Table
 *
 * Table component for displaying POS receipts using DataGrid.
 * Uses server-side pagination with our custom Pagination component.
 */

import { useMemo, useCallback } from 'react';
import { Receipt, Check, AlertTriangle } from 'lucide-react';
import {
  DataGrid,
  EmptyState,
  textColumn,
  dateTimeColumn,
  moneyColumn,
  integerColumn,
  type DataGridColumn,
  type DataGridSortState,
} from '@bill/ui';
import { useCurrency, useFormatDate } from '@/hooks';
import { formatCentimes } from '@/lib/format';
import type { PosReceiptListItem, PaginationInfo } from '@bstock/shared';

export type SortField = 'createdAt' | 'orderId' | 'amountExclTax';
export type SortOrder = 'asc' | 'desc';

const SORT_FIELD_MAP: Record<string, SortField> = {
  createdAt: 'createdAt',
  orderId: 'orderId',
  amountExclTax: 'amountExclTax',
};

interface ReceiptsTableProps {
  receipts: PosReceiptListItem[];
  pagination: PaginationInfo;
  isLoading?: boolean;
  onRowClick: (receiptId: string) => void;
  onPageChange: (page: number) => void;
  onLimitChange?: (limit: number) => void;
  sortBy?: SortField;
  sortOrder?: SortOrder;
  onSort?: (field: SortField, order: SortOrder) => void;
  showSiteColumn?: boolean;
  showFinancials?: boolean;
}

export function ReceiptsTable({
  receipts,
  pagination,
  isLoading,
  onRowClick,
  onPageChange,
  onLimitChange,
  sortBy = 'createdAt',
  sortOrder = 'desc',
  onSort,
  showSiteColumn = false,
  showFinancials = false,
}: ReceiptsTableProps) {
  const currency = useCurrency();
  const { formatDateShort, formatTime } = useFormatDate();

  const columns = useMemo<DataGridColumn<PosReceiptListItem>[]>(() => {
    const cols: DataGridColumn<PosReceiptListItem>[] = [];

    if (showSiteColumn) {
      cols.push(
        textColumn<PosReceiptListItem>({
          id: 'brandName',
          header: 'Site',
          sortable: false,
          accessor: 'brandName',
        })
      );
    }

    cols.push(
      dateTimeColumn<PosReceiptListItem>({
        id: 'createdAt',
        header: 'Cree le',
        accessor: (r) => r.createdAt,
        cell: (r) => (
          <div>
            <div className="text-sm text-gray-900">{formatDateShort(r.createdAt)}</div>
            <div className="text-xs text-gray-500">{formatTime(r.createdAt)}</div>
          </div>
        ),
        exportValue: (r) => r.createdAt,
      }),
      textColumn<PosReceiptListItem>({
        id: 'orderId',
        header: 'Order ID',
        accessor: 'orderId',
        cell: (r) => (
          <div>
            <span className={`text-sm font-medium ${r.isCancel ? 'text-red-600' : 'text-blue-600'}`}>
              {r.orderId}
            </span>
            {r.isCancel && (
              <div className="text-xs text-red-500 font-medium">Annulee</div>
            )}
          </div>
        ),
      }),
      textColumn<PosReceiptListItem>({
        id: 'posTerminal',
        header: 'Terminal POS',
        sortable: false,
        accessor: (r) => r.posTerminal || '-',
      }),
      textColumn<PosReceiptListItem>({
        id: 'posUsername',
        header: 'Utilisateur',
        sortable: false,
        accessor: (r) => r.posUsername || '-',
      }),
      textColumn<PosReceiptListItem>({
        id: 'itemsWithRecipeCount',
        header: 'Recettes traitees',
        sortable: false,
        align: 'center',
        accessor: (r) => `${r.itemsWithRecipeCount} / ${r.itemsCount}`,
        cell: (r) => {
          const isComplete = r.itemsWithRecipeCount === r.itemsCount;
          const allZero = r.itemsCount === 0;
          return (
            <span className={allZero ? 'text-gray-400' : isComplete ? 'text-green-600 font-medium' : 'text-orange-600'}>
              {r.itemsWithRecipeCount} / {r.itemsCount}
            </span>
          );
        },
      })
    );

    if (showFinancials) {
      cols.push(
        moneyColumn<PosReceiptListItem>({
          id: 'amountExclTax',
          header: 'Total HT',
          accessor: (r) => r.amountExclTax / 100,
          cell: (r) => (
            <span className="text-sm text-gray-900">{formatCentimes(r.amountExclTax, currency)}</span>
          ),
          exportValue: (r) => r.amountExclTax / 100,
        }),
        moneyColumn<PosReceiptListItem>({
          id: 'amountInclTax',
          header: 'Total TTC',
          sortable: false,
          accessor: (r) => r.amountInclTax / 100,
          cell: (r) => (
            <span className="text-sm font-medium text-gray-900">{formatCentimes(r.amountInclTax, currency)}</span>
          ),
          exportValue: (r) => r.amountInclTax / 100,
        })
      );
    }

    cols.push(
      integerColumn<PosReceiptListItem>({
        id: 'hasMovements',
        header: 'Mouvements',
        sortable: false,
        align: 'center',
        width: '160px',
        accessor: (r) => (r.hasMovements ? 1 : 0),
        cell: (r) =>
          r.hasMovements ? (
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-600">
              <Check className="w-4 h-4" />
            </span>
          ) : (
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-orange-100 text-orange-600">
              <AlertTriangle className="w-4 h-4" />
            </span>
          ),
        exportValue: (r) => (r.hasMovements ? 'Oui' : 'Non'),
      })
    );

    return cols;
  }, [showSiteColumn, showFinancials, currency, formatDateShort, formatTime]);

  const sortState = useMemo<DataGridSortState | null>(() => {
    if (!sortBy || !sortOrder) return null;
    const columnId = Object.entries(SORT_FIELD_MAP).find(([, v]) => v === sortBy)?.[0];
    if (!columnId) return null;
    return { columnId, direction: sortOrder };
  }, [sortBy, sortOrder]);

  const handleSortChange = useCallback(
    (next: DataGridSortState | null) => {
      if (!onSort) return;
      if (!next) {
        onSort('createdAt', 'desc');
        return;
      }
      const backendField = SORT_FIELD_MAP[next.columnId];
      if (backendField) onSort(backendField, next.direction);
    },
    [onSort]
  );

  if (!isLoading && receipts.length === 0) {
    return (
      <EmptyState
        icon={Receipt}
        title="Aucun ticket trouve"
        description="Aucun ticket ne correspond a vos criteres de recherche. Essayez de modifier vos filtres."
      />
    );
  }

  return (
    <DataGrid<PosReceiptListItem>
      data={receipts}
      columns={columns}
      rowKey={(r) => r.id}
      isLoading={isLoading}
      skeletonRows={10}
      onRowClick={(r) => onRowClick(r.id)}
      currency={currency}
      sortState={sortState}
      onSortChange={handleSortChange}
      exportable={{
        fileName: { base: 'tickets-caisse' },
        sheetName: 'Tickets Caisse',
        position: 'header',
      }}
      pagination={{
        page: pagination.page,
        limit: pagination.limit,
        total: pagination.total,
        totalPages: pagination.totalPages,
        onPageChange,
        onLimitChange,
      }}
      emptyState={
        <div className="py-8 text-center text-sm text-neutral-500">
          Aucun ticket trouve. Essayez de modifier vos filtres.
        </div>
      }
    />
  );
}
